import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { requestCurrentUser } from "../../../actions/user";
import {
  dismissAccountPasswordPrompt,
  setAccountPassword,
} from "../../../api/user";
import { getMockUser, isImpersonating } from "../../../helpers/impersonation";
import {
  clearPasswordOffer,
  hasPasswordOffer,
  needsOwnPassword,
  passwordPromptSnoozed,
} from "../../../helpers/shopifyLoginIntent";
import useTheme from "../../../helpers/useTheme";
import { pickOnBrandTextColor } from "../../../pages/authentication/authScreenUtils";

const MIN_PASSWORD_LENGTH = 8;

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

/**
 * Optional "set a password" card. Shown only after the seller signed in
 * with Shopify from the login page, on an account a Shopify install made
 * (no password of its own). Never blocks anything; "Not now" snoozes it
 * for 30 days.
 */
export default function SetPasswordPrompt() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.currentUser?.user);
  const { primaryColor } = useTheme();
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const show =
    !hidden &&
    !!user?.uid &&
    (saved ||
      (hasPasswordOffer() &&
        needsOwnPassword(user) &&
        !passwordPromptSnoozed(user) &&
        !isImpersonating(getMockUser())));
  if (!show) return null;

  const brand = primaryColor || "#14110E";
  const onBrand = pickOnBrandTextColor(brand) === "light" ? "#fff" : "#14110E";

  const close = () => {
    clearPasswordOffer();
    setHidden(true);
  };

  const notNow = async () => {
    setBusy(true);
    try {
      await dismissAccountPasswordPrompt();
    } catch (_) {
      /* still hide it for this session */
    }
    close();
  };

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await setAccountPassword(password);
      clearPasswordOffer();
      setSaved(true);
      setPassword("");
      setConfirm("");
      dispatch(requestCurrentUser());
    } catch (err) {
      setError(errorText(err, "Could not save your password. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const card = {
    margin: "12px 16px 0",
    padding: "14px 16px",
    borderRadius: 10,
    border: "1px solid var(--bs-border-color, rgba(20, 17, 14, 0.12))",
    background: "var(--bs-body-bg, #fff)",
    color: "inherit",
    fontFamily: "inherit",
  };
  const primaryButton = {
    background: brand,
    color: onBrand,
    border: 0,
    borderRadius: 8,
    padding: "8px 14px",
    fontWeight: 600,
    fontFamily: "inherit",
  };
  const quietButton = {
    background: "transparent",
    color: "inherit",
    border: "1px solid rgba(20, 17, 14, 0.2)",
    borderRadius: 8,
    padding: "8px 14px",
    fontFamily: "inherit",
  };
  const input = {
    width: "100%",
    maxWidth: 280,
    height: 38,
    padding: "0 10px",
    borderRadius: 8,
    border: "1px solid rgba(20, 17, 14, 0.2)",
    fontFamily: "inherit",
  };

  if (saved) {
    return (
      <div role="status" style={{ ...card, display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <p style={{ margin: 0 }}>
          Password saved. Next time you can sign in with your email and this
          password.
        </p>
        <button type="button" style={quietButton} onClick={() => setHidden(true)}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div role="region" aria-label="Set a password" style={card}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div>
          <p style={{ margin: 0, fontWeight: 600 }}>
            Want to sign in without your store?
          </p>
          <p style={{ margin: "2px 0 0", opacity: 0.75 }}>
            Set a password to sign in with {user?.email || "your email"} next
            time. You can keep using Sign in with Shopify either way.
          </p>
        </div>
        {!expanded && (
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" style={primaryButton} disabled={busy} onClick={() => setExpanded(true)}>
              Set password
            </button>
            <button type="button" style={quietButton} disabled={busy} onClick={notNow}>
              Not now
            </button>
          </span>
        )}
      </div>

      {expanded && (
        <form onSubmit={save} style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* Lets password managers save it against the right account. */}
          <input type="email" name="username" autoComplete="username" value={user?.email || ""} readOnly hidden />
          <input
            type="password"
            name="new-password"
            autoComplete="new-password"
            placeholder="New password"
            aria-label="New password"
            style={input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          <input
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            placeholder="Confirm password"
            aria-label="Confirm password"
            style={input}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <button type="submit" style={primaryButton} disabled={busy || !password || !confirm}>
            {busy ? "Saving…" : "Save password"}
          </button>
          <button type="button" style={quietButton} disabled={busy} onClick={notNow}>
            Not now
          </button>
          {error ? (
            <p role="alert" style={{ margin: 0, width: "100%", color: "#b42318" }}>
              {error}
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
}
